import React, { useEffect, useMemo } from "react";
import "./WeeklyReport.css";
import CustomSelect from "../../../../components/custom-select/CustomSelect";
import { useParams, useNavigate } from "react-router-dom";
import Breadcrumb from "../../../../components/custom-breadcrumb/Breadcrumb";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import { fetchCmsReport } from "../../../../redux/slices/userListSlice";
import { formatDateTime } from "../../../../utils/formatDateTime";
import Loader from "../../../../components/custom-loader/Loader";

const WeeklyReport: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { name, reportId } = useParams<{
    name: string;
    reportId: string;
  }>();

  const { cmsReport, cmsReportLoading } = useSelector(
    (state: RootState) => state.userList,
  );

  /* ================================
     FETCH REPORT
  ================================= */
  useEffect(() => {
    if (reportId) {
      dispatch(fetchCmsReport({ weekReportMappingId: reportId }));
    }
  }, [dispatch, reportId]);

  /* ================================
     Transform API → UI Structure
  ================================= */
  const sections = useMemo(() => {
    if (!cmsReport) return [];

    return [...cmsReport.reportDetails] // ✅ clone first
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((section) => ({
        id: section.id,
        title: section.name,
        questions: [...section.questions] // ✅ clone
          .sort((a, b) => a.questionSortOrder - b.questionSortOrder)
          .map((q) => ({
            id: q.questionId,
            text: q.questionName,
            options: [...q.options] // ✅ clone
              .sort((a, b) => a.optionSortOrder - b.optionSortOrder)
              .map((opt) => ({
                id: opt.optionId,
                text: opt.optionName,
                selected: opt.optionName === q.answer,
              })),
            // options: [...q.options]
            // .sort((a, b) => a.optionSortOrder - b.optionSortOrder)
            // .map((opt) => ({
            //   id: opt.optionId,
            //   text: opt.optionName,
            //   selected: opt.optionId === q.answer, // ✅ compare by ID
            // })),
          })),
      }));
  }, [cmsReport]);

  const breadcrumbItems = [
    { label: "Users", onClick: () => navigate("/users") },
    { label: name ?? "", onClick: () => navigate(`/users/${name}`) },
    { label: "Weekly Report", disabled: true },
  ];

  if (cmsReportLoading) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  return (
    <>
      <Breadcrumb items={breadcrumbItems} />

      <div
        className="weekly-report-header"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "15px",
        }}
      >
        <h1 className="page-title">Weekly Report</h1>

        {/* <CustomSelect
          options={
            cmsReport?.reports?.weeks?.name
              ? [cmsReport.reports.weeks.name]
              : []
          }
          defaultValue={cmsReport?.reports?.weeks?.name}
        /> */}
      </div>

      <div className="weekly-report">
        <div className="report-header">
          <div className="report-header-top">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginBottom: "8px",
              }}
            >
              <h2 className="report-title">
                Weekly Progress Report - {cmsReport?.reports?.weeks?.name}
              </h2>
            </div>

            <div className="report-meta">
              Submitted On: {formatDateTime(cmsReport?.createdAt || "")}
            </div>
          </div>
        </div>

        {sections.map((section) => (
          <section
            className="report-section"
            key={section.id}
            aria-labelledby={`section-${section.id}`}
          >
            <div className="section-header" id={`section-${section.id}`}>
              <div className="section-left">
                <h3 className="section-title">{section.title}</h3>
              </div>
            </div>

            <div className="section-body">
              {section.questions.map((q: any, index: number) => (
                <div className="question" key={q.id}>
                  <div className="q-text">
                    <span className="q-number">Q{index + 1}.</span>
                    <span className="q-label">{q.text}</span>
                  </div>

                  <div className="chips" role="list">
                    {q.options
                      .filter((opt: any) => opt.text?.trim() !== "")
                      .map((opt: any) => (
                        <span
                          key={opt.id}
                          role="listitem"
                          className={`chip ${opt.selected ? "selected" : ""}`}
                          aria-pressed={opt.selected ? "true" : "false"}
                        >
                          {opt.text}
                        </span>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </>
  );
};

export default WeeklyReport;
